import { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'core/buttons/Button';
import TransactionLogs from './TransactionLogs';
import SpringModal from 'core/modal/SpringModal';
import ActiveBadge from 'core/badges/ActiveBadge';
import VerifiedBadge from 'core/badges/VerifiedBadge';
import GroupedTableView from 'core/table/GroupedTableView';
import RejectTransactionForm from './RejectTransactionForm';
import TransactionWebhookLogsModal from './TransactionWebhookLogsModal';
import ApproveTransactionForm from './ApproveTransactionForm';
import PartnerClickAwayDetails from 'components/partners/PartnerClickAwayDetails';
import CustomerClickAwayDetails from 'components/customers/CustomerClickAwayDetails';

import Auth from 'utils/Auth';
import isEmpty from 'helpers/isEmpty';
import DateUtils from 'utils/DateUtils';
import useCountries from 'hooks/useCountries';
import { fundingSources } from 'app/config/FundingSource';
import { transactionStatuses } from 'app/config/TransactionStatus';
import {
  approveTransaction,
  getTransaction,
  rejectTransaction,
} from 'app/features/transactions/transactionSaga';
import Clipboard from 'core/clipboard/Clipboard';
import ErrorModal from './ErrorModal';

import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';

export default function Transaction({ transactionId, transaction, isLoading }) {
  const dispatch = useDispatch();

  const [openApproveTransactionModal, setOpenApproveTransactionModal] = useState(false);
  const [openRejectTransactionModal, setOpenRejectTransactionModal] = useState(false);

  const hideApproveTransactionModal = useCallback(() => setOpenApproveTransactionModal(false), []);
  const hideRejectTransactionModal = useCallback(() => setOpenRejectTransactionModal(false), []);

  const { countries, getCountryNameByIso3Code } = useCountries();

  const viewTransactionData = useMemo(
    () => [
      {
        groupName: 'Brief Details',
        groupData: [
          {
            label: 'Transaction Id',
            data: 'id',
          },
          {
            label: 'Order Id',
            data: 'order_id',
          },

          {
            label: 'Partner',
            data: 'partner_id',
            get: ({ partner_id, partner_name }) => (
              <PartnerClickAwayDetails partnerId={partner_id} label={partner_name} />
            ),
          },

          {
            label: 'Pin Number',
            data: 'pin_number',
          },
          {
            label: 'Funding Id',
            data: 'funding_id',
          },
          {
            label: 'Funding Type',
            data: 'funding_type',
          },
          {
            label: 'Sending Country',
            get: ({ sending_country }) => getCountryNameByIso3Code(sending_country),
          },
          {
            label: 'Sending Amount',
            data: 'sending_amount',
          },
          {
            label: 'Sending Currency',
            data: 'sending_currency',
          },
          {
            label: 'Payout Country',
            get: ({ payout_country }) => getCountryNameByIso3Code(payout_country),
          },
          {
            label: 'Payout Amount',
            data: 'payout_amount',
          },
          {
            label: 'Payout Currency',
            data: 'payout_currency',
          },
          {
            label: 'Purpose',
            data: 'txn_purpose',
          },
          {
            label: ' Payout Status',
            data: 'status',
          },
          {
            label: 'Transaction/Reference ID',
            data: 'transaction_reference_number',
            get: ({ transaction_reference_number }) =>
              transaction_reference_number ? (
                <Clipboard content={transaction_reference_number} />
              ) : (
                'N/A'
              ),
          },
          {
            label: 'Remarks',
            data: 'remarks',
          },
          {
            label: 'Is Active ?',
            data: 'is_active',
            get: ({ is_active }) => <ActiveBadge isActive={!!is_active} />,
          },
          {
            label: 'Is Verified ?',
            data: 'is_verified',
            get: ({ is_verified }) => <VerifiedBadge isVerified={!!is_verified} />,
          },

          {
            label: 'Created At',
            data: 'created_ts',
            get: ({ created_ts }) =>
              !isEmpty(created_ts) ? DateUtils.isoToString(created_ts) : '-',
          },
          {
            label: 'Created By',
            data: 'created_by',
          },
          {
            label: 'Updated At',
            data: 'updated_ts',
            get: ({ updated_ts }) =>
              !isEmpty(updated_ts) ? DateUtils.isoToString(updated_ts) : '-',
          },
          {
            label: 'Updated By',
            data: 'updated_by',
          },
        ],
      },
      {
        groupName: 'Customer Details',
        groupData: [
          {
            label: 'Customer',
            data: 'user_name',
          },
          {
            label: 'Customer ID',
            data: 'user_id',
            get: ({ user_id }) => <CustomerClickAwayDetails customerId={user_id} />,
          },
          {
            label: 'ID Type',
            data: 'id_type',
          },
          {
            label: 'Email',
            data: 'email',
          },
          {
            label: 'Date of Birth',
            data: 'dob',
          },
          {
            label: 'Mobile Number',
            data: 'mobile_number',
          },
          {
            label: 'Suburb',
            data: 'suburb',
          },
          {
            label: 'Street Type',
            data: 'street',
          },
          {
            label: 'Street Number',
            data: 'street_number',
          },
        ],
      },
      {
        groupName: 'Beneficiary Details',
        groupData: [
          {
            label: 'ID',
            data: 'beneficiary_id',
          },
          {
            label: 'Name',
            data: 'beneficiay_name',
          },
          {
            label: 'Type',
            data: 'beneficiary_type',
          },
          {
            label: 'Relation',
            data: 'beneficiary_relation',
          },
          {
            label: 'Address',
            data: 'beneficiary_address',
          },
          {
            label: 'Country',
            get: ({ beneficiary_country }) => getCountryNameByIso3Code(beneficiary_country),
          },
          {
            label: 'Currency',
            data: 'beneficiary_currency',
          },
          {
            label: 'Mobile Number',
            data: 'beneficiary_mobile_number',
          },
          {
            label: 'Payment Type',
            data: 'beneficiary_payment_type',
          },
          {
            label: 'Account Number',
            data: 'beneficiary_account_number',
          },
          {
            label: 'Branch',
            data: 'beneficiary_branch',
          },
        ],
      },
    ],
    [countries],
  );

  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const handleApproveTransaction = (data) => {
    dispatch(
      approveTransaction({
        transactionId,
        ...data,
        onError: (msg) => {
          setError(msg);
          setModalOpen(true);
        },
      }),
    );
  };

  const handleRejectTransaction = (data) => {
    dispatch(
      rejectTransaction({
        ...data,
        transactionId,
      }),
    );
  };
  // const { isLoading, data: transaction } = useSelector((state) => state.transactions.show);

  const {isLoading: isApproving, isSuccess: isApproveSuccess } = useSelector(
    (state) => state.transactions.approve,
  );
  const { isLoading: isRejecting, isSuccess: isRejectSuccess } = useSelector(
    (state) => state.transactions.reject,
  );

  useEffect(() => {
    if (isRejectSuccess) {
      setOpenRejectTransactionModal(false);
    }
  }, [isRejectSuccess]);

  useEffect(() => {
    if (isApproveSuccess) {
      setOpenApproveTransactionModal(false);
    }
  }, [isApproveSuccess]);

  const rejectFundingTypes = [
    // fundingSources.payNowQR.toUpperCase(),
    fundingSources.poli.toUpperCase(),
  ];


  return (
    <>
      <Grid container spacing={2}>
        {viewTransactionData.map((group, i) => (
          <GroupedTableView
            key={i}
            title={group.groupName}
            data={group.groupData}
            item={transaction}
            isLoading={isLoading}
          />
        ))}
      </Grid>

      {!isLoading && transaction && (
        <Box display="flex" gap={3}>
          {transaction.status.toUpperCase() === transactionStatuses.created.toUpperCase() && (
            <>
              {!rejectFundingTypes.includes(transaction.funding_type.toUpperCase()) && (
                <>
                  <HasPermission permission={permissions.UPDATE_TRANSACTION_STATUS}>
                    <Button color="success" onClick={() => setOpenApproveTransactionModal(true)}>
                      Approve
                    </Button>
                  </HasPermission>

                  <SpringModal
                    header="Approve Transaction"
                    isOpen={openApproveTransactionModal}
                    onClose={() => setOpenApproveTransactionModal(false)}
                    enableMaximizeButton={false}
                    enableDefaultPadding
                    sx={{ maxWidth: '600px' }}
                  >
                    <ApproveTransactionForm
                      onSubmit={handleApproveTransaction}
                      onCancel={hideApproveTransactionModal}
                    />

                    <ErrorModal
                      open={modalOpen}
                      onClose={() => setModalOpen(false)}
                      message={error}
                    />
                  </SpringModal>
                </>
              )}
            </>
          )}

          {(transaction.status.toUpperCase() === transactionStatuses.created.toUpperCase() ||
            transaction.status.toUpperCase() === transactionStatuses.pending.toUpperCase()) && (
            <>
              <HasPermission permission={permissions.UPDATE_TRANSACTION_STATUS}>
                <Button color="error" onClick={() => setOpenRejectTransactionModal(true)}>
                  Reject
                </Button>
              </HasPermission>
              <SpringModal
                header="Reject Transaction"
                isOpen={openRejectTransactionModal}
                onClose={() => setOpenRejectTransactionModal(false)}
                enableMaximizeButton={false}
                enableDefaultPadding
                sx={{ maxWidth: '600px' }}
              >
                <RejectTransactionForm
                  onSubmit={handleRejectTransaction}
                  onCancel={hideRejectTransactionModal}
                />
              </SpringModal>
            </>
          )}

          <TransactionWebhookLogsModal orderId={transaction.order_id} />
        </Box>
      )}

    </>
  );
}

Transaction.propTypes = {
  transactionId: PropTypes.string,
};
